import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getCountries } from "../../../services/country";
import { localStoragePrefix } from "../../../utils/GeneralFunc";

const STORAGE_KEY = `${localStoragePrefix}selectedCountryCode`;
const FETCH_TIMEOUT_MS = 5000;

const clean = (v) => (typeof v === "string" ? v.replace(/,$/, "") : v);

const getStoredCode = () => {
  try {
    return typeof localStorage !== "undefined"
      ? localStorage.getItem(STORAGE_KEY)
      : null;
  } catch {
    return null;
  }
};

const setStoredCode = (code) => {
  try {
    if (code) localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // ignore write errors (private mode / SSR)
  }
};

// Fallback only — reflects browser/OS language, NOT real location
const detectCountryFromLocale = () => {
  if (typeof navigator === "undefined") return null;
  const locales =
    navigator.languages && navigator.languages.length
      ? navigator.languages
      : [navigator.language];

  for (const locale of locales) {
    if (!locale) continue;
    const region = locale.split("-")[1];
    if (region) return region.toUpperCase();
  }
  return null;
};

const fetchWithTimeout = async (url, ms) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    return await fetch(url, { signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
};

// Multiple free providers, tried in order. Each parser returns an
// uppercase ISO country code, or throws if it can't find one.
const IP_GEO_PROVIDERS = [
  {
    url: "https://ipwho.is/",
    parse: (data) => {
      if (data.success === false || !data.country_code) {
        throw new Error("ipwho.is: no country_code");
      }
      return data.country_code.toUpperCase();
    },
  },
  {
    url: "https://get.geojs.io/v1/ip/country.json",
    parse: (data) => {
      if (!data.country) throw new Error("geojs.io: no country");
      return data.country.toUpperCase(); // already a 2-letter code
    },
  },
  {
    url: "https://ipapi.co/json/",
    parse: (data) => {
      if (!data.country_code) throw new Error("ipapi.co: no country_code");
      return data.country_code.toUpperCase();
    },
  },
];

// Real location-based detection via IP lookup, with provider fallback chain
export const detectUserCountry = createAsyncThunk(
  "country/detectUserCountry",
  async () => {
    let lastError = null;

    for (const provider of IP_GEO_PROVIDERS) {
      try {
        const res = await fetchWithTimeout(provider.url, FETCH_TIMEOUT_MS);
        if (!res.ok) throw new Error(`${provider.url} -> ${res.status}`);
        const data = await res.json();
        return provider.parse(data);
      } catch (err) {
        lastError = err;
        // try next provider
      }
    }

    throw lastError || new Error("All IP geolocation providers failed");
  }
);

export const fetchCountries = createAsyncThunk(
  "country/fetchCountries",
  async () => {
    const data = await getCountries();
    return Object.fromEntries(
      Object.entries(data).map(([code, info]) => [
        code.toUpperCase(),
        {
          ...info,
          flagEmoji: clean(info.flagEmoji),
          dialCode: clean(info.dialCode),
        },
      ])
    );
  }
);

const storedCode = getStoredCode();

const initialState = {
  countries: {},
  selectedCode: storedCode || "US", // placeholder until detection/fetch resolve
  hasExplicitSelection: !!storedCode,
  detectedCode: null,
  status: "idle", // idle | loading | succeeded | failed
  detectStatus: "idle", // idle | loading | succeeded | failed
  error: null,
};

// Priority: explicit/stored choice > IP-detected country > browser locale > US > first available
const resolveSelectedCode = (state) => {
  const { countries, hasExplicitSelection, selectedCode, detectedCode } = state;
  if (Object.keys(countries).length === 0) return selectedCode;

  if (hasExplicitSelection && countries[selectedCode]) {
    return selectedCode;
  }
  if (detectedCode && countries[detectedCode]) {
    return detectedCode;
  }
  const fallbackLocale = detectCountryFromLocale();
  if (fallbackLocale && countries[fallbackLocale]) {
    return fallbackLocale;
  }
  return countries.US ? "US" : Object.keys(countries)[0];
};

const countrySlice = createSlice({
  name: "country",
  initialState,
  reducers: {
    setSelectedCountry: (state, action) => {
      const code = action.payload;
      if (!state.countries[code]) return;
      state.selectedCode = code;
      state.hasExplicitSelection = true;
      setStoredCode(code);
    },
    resetToAutoDetect: (state) => {
      state.hasExplicitSelection = false;
      state.selectedCode = resolveSelectedCode(state);
      setStoredCode(state.selectedCode);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCountries.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.countries = action.payload;
        state.selectedCode = resolveSelectedCode(state);
        if (!state.hasExplicitSelection) setStoredCode(state.selectedCode);
      })
      .addCase(fetchCountries.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })

      .addCase(detectUserCountry.pending, (state) => {
        state.detectStatus = "loading";
      })
      .addCase(detectUserCountry.fulfilled, (state, action) => {
        state.detectStatus = "succeeded";
        state.detectedCode = action.payload;
        state.selectedCode = resolveSelectedCode(state);
        if (!state.hasExplicitSelection) setStoredCode(state.selectedCode);
      })
      .addCase(detectUserCountry.rejected, (state, action) => {
        state.detectStatus = "failed";
        state.error = action.error.message;
        if (!state.hasExplicitSelection && !state.detectedCode) {
          state.selectedCode = resolveSelectedCode(state);
        }
      });
  },
});

// selectors
export const selectCountries = (state) => state.country.countries;
export const selectSelectedCode = (state) => state.country.selectedCode;
export const selectSelectedCountry = (state) => {
  const code = state.country.selectedCode;
  const info = state.country.countries[code];
  return info ? { ...info, code } : undefined;
};
export const selectCountryStatus = (state) => state.country.status;
export const selectCountryDetectStatus = (state) => state.country.detectStatus;

export const countryAction = countrySlice.actions;
export default countrySlice;

// import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
// import { getCountries } from "../../../services/country";


// const STORAGE_KEY = "selectedCountryCode";


// const clean = (v) => (typeof v === "string" ? v.replace(/,$/, "") : v);

// export const fetchCountries = createAsyncThunk(
//   "country/fetchCountries",
//   async () => {
//     const data = await getCountries();

//     return Object.fromEntries(
//       Object.entries(data).map(([code, info]) => [
//         code.toUpperCase(),
//         {
//           ...info,
//           flagEmoji: clean(info.flagEmoji),
//           dialCode: clean(info.dialCode),
//         },
//       ])
//     );
//   }
// );

// const initialState = {
//   countries: {},
//   selectedCode: localStorage.getItem(STORAGE_KEY) || "US",
//   status: "idle", // idle | loading | succeeded | failed
//   error: null,
// };

// const countrySlice = createSlice({
//   name: "country",
//   initialState,
//   reducers: {
//     setSelectedCountry: (state, action) => {
//       const code = action.payload;
//       if (!state.countries[code]) return;
//       state.selectedCode = code;
//       localStorage.setItem(STORAGE_KEY, code);
//     },
//   },
//   extraReducers: (builder) => {
//     builder
//       .addCase(fetchCountries.pending, (state) => {
//         state.status = "loading";
//       })
//       .addCase(fetchCountries.fulfilled, (state, action) => {
//         state.status = "succeeded";
//         state.countries = action.payload;
//         if (!state.countries[state.selectedCode]) {
//           state.selectedCode = state.countries.us
//             ? "US"
//             : Object.keys(state.countries)[0];
//           localStorage.setItem(STORAGE_KEY, state.selectedCode);
//         }
//       })
//       .addCase(fetchCountries.rejected, (state, action) => {
//         state.status = "failed";
//         state.error = action.error.message;
//       });
//   },
// });



// // selectors
// export const selectCountries = (state) => state.country.countries;
// export const selectSelectedCode = (state) => state.country.selectedCode;
// export const selectSelectedCountry = (state) => {
//   const code = state.country.selectedCode;
//   const info = state.country.countries[code];
//   return info ? { ...info, code } : undefined;
// };
// export const selectCountryStatus = (state) => state.country.status;


// export const countryAction = countrySlice.actions;
// export default countrySlice;